<template>
  <PageWrapper>
    <template #content>
      <div class="p-8">
        <div id="domain-search" class="@container max-w-6xl mx-auto">
          <form
            id="domain-search-bar"
            class="flex flex-col gap-2 mt-4 p-2 bg-gray-50 border-2 border-gray-200 rounded-xl"
            @submit.prevent="search"
          >
            <fieldset class="grid grid-cols-1 @sm:grid-cols-12 gap-2">
              <div class="@sm:col-span-12 relative">
                <MagnifyingGlass
                  class="pointer-events-none w-6 h-6 absolute top-5.5 left-1 m-1 fill-gray-300"
                />
                <label for="domain-name" class="block truncate">
                  Find a domain
                </label>
                <input
                  type="text"
                  id="domain-name"
                  aria-label="Domain Name"
                  placeholder="example.com"
                  v-model="searchParams.name"
                  class="w-full h-10 pl-10 transition-colors bg-white"
                  ref="textInput"
                />
              </div>
              <div class="@sm:col-span-4 @3xl:col-span-2">
                <label for="sortby" class="block truncate"> Order by </label>
                <select
                  name="sortby"
                  aria-label="Sort by"
                  class="w-full bg-white"
                  v-model="searchParams.sort_by"
                >
                  <option value="name">Name</option>
                  <option value="last_cmp_dco_date">Last Update</option>
                </select>
              </div>
              <div class="@sm:col-span-4 @3xl:col-span-2 relative">
                <label for="direction" class="block truncate">
                  Direction
                </label>
                <select
                  name="direction"
                  aria-label="direction"
                  class="w-full peer bg-white"
                  v-model="searchParams.direction"
                >
                  <option value="ascending">Ascending</option>
                  <option value="descending">Descending</option>
                </select>
              </div>
              <div
                class="@sm:col-span-4 @3xl:col-span-3 relative overflow-hidden"
              >
                <label for="direction" class="block truncate">
                  Agent Permission Status*
                </label>
                <select
                  name="direction"
                  aria-label="direction"
                  class="w-full peer bg-white"
                  v-model="searchParams.vendor_approved"
                >
                  <option value="all">Any</option>
                  <option value="approved">Granted</option>
                  <option value="not-approved">Not granted</option>
                </select>
              </div>
              <div
                class="@sm:col-span-12 mt-2 @3xl:col-span-5 self-end flex flex-col @sm:flex-row justify-end gap-2 text-center"
              >
                <button
                  class="text-white bg-blue-600 hover:bg-blue-800"
                  type="submit"
                >
                  Search
                </button>
                <a
                  class="p-2 rounded-md text-white bg-gray-600 hover:bg-gray-800 hover:no-underline"
                  download="domains.csv"
                  :href="csvHref"
                >
                  Download as CSV
                </a>
              </div>
            </fieldset>
          </form>
          <hr class="my-4 border-gray-300" />
          <div
            v-if="isMirusUser && vendorKey === 'all'"
            class="rounded-lg px-2 py-1 mb-4 mx-auto border-2 border-dashed border-orange-400 bg-orange-50 text-orange-900 text-sm max-w-xl"
          >
            <span class="font-bold">Note:</span> Showing domains enrolled with
            any vendor. Please select a vendor to see more detailed results.
          </div>
          <div class="w-full text-center mt-12" v-if="loadingDomains">
            <LoadingSpinner />
          </div>
          <div class="text-gray-500" v-else-if="domains.length === 0">
            <div class="text-center font-semibold">No results</div>
            <div
              class="mx-auto max-w-xl mt-3 text-xs px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-lg"
            >
              <div>
                <span class="font-bold">Note: </span>If you do not find the
                domain you are looking for, first check to see if you entered it
                correctly. If you did and it is still not appearing, the agent
                may not be enrolled for the program.
              </div>

              <div class="mt-2">
                <span class="font-bold"
                  >* Agent Permission Status options:</span
                >
                <ul class="list-disc list-inside pl-2">
                  <li>
                    <span class="font-semibold">Any:</span> Search for any
                    domain enrolled with {{ vendor }}.
                  </li>
                  <li>
                    <span class="font-semibold">Granted:</span>
                    Search for domains which have granted
                    {{ vendor }} permissions in MySFDomain.
                  </li>
                  <li>
                    <span class="font-semibold">Not granted:</span>
                    Search for domains which are enrolled with {{ vendor }} but
                    have <span class="font-bold">not</span> granted
                    {{ vendor }} permissions in MySFDomain.
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div v-else>
            <DomainResultList :domains="domains" />
          </div>
          <div v-if="domains.length && !loadingDomains" class="mt-2">
            <button
              class="text-white w-full mt-2"
              v-if="nextPageLink != null"
              @click.prevent="loadNextPage"
            >
              <LoadingSpinner v-if="loadingNextPage" gray small />
              <span v-else> Show More </span>
            </button>
            <div v-else class="mt-2 w-full text-center text-gray-500">
              No more results
            </div>
          </div>
        </div>
      </div>
      <Onboarding page="search" />
    </template>
  </PageWrapper>
</template>

<script setup>
import { useUrlSearchParams } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, ref } from "vue";
import DomainResultList from "@/components/DomainResultList.vue";
import MagnifyingGlass from "@/components/icons/MagnifyingGlass.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Onboarding from "@/components/Onboarding.vue";
import PageWrapper from "@/components/PageWrapper.vue";
import useUserStore from "@/stores/user";

const searchParams = useUrlSearchParams("history", {
  initialValue: {
    name: "",
    sort_by: "name",
    direction: "ascending",
    vendor_approved: "all",
  },
});

const userStore = useUserStore();
const { vendor, vendorKey, isMirusUser } = storeToRefs(userStore);

const nextPageLink = ref(null);
const loadingNextPage = ref(false);
const textInput = ref(null);

const domains = ref([]);
const loadingDomains = ref(false);

const apiRequestParams = computed(() => {
  let params = {
    name: searchParams.name,
    sort_by: searchParams.sort_by,
    direction: searchParams.direction,
  };

  switch (searchParams.vendor_approved) {
    case "approved":
      params.vendor_approved = true;
      break;
    case "not-approved":
      params.vendor_approved = false;
      break;
  }

  return params;
});

async function search() {
  loadingDomains.value = true;
  const params = new URLSearchParams(apiRequestParams.value);
  const response = await fetch(
    `/api/cmp/${vendorKey.value}/domains/?${params.toString()}`,
  );
  if (!response.ok) {
    throw new Error("Domain search failed");
  }
  const data = await response.json();
  nextPageLink.value = data.next;
  domains.value = data.results;

  loadingDomains.value = false;
}

async function loadNextPage() {
  loadingNextPage.value = true;
  const response = await fetch(nextPageLink.value);
  if (!response.ok) {
    throw new Error("Domain search failed");
  }
  const data = await response.json();
  nextPageLink.value = data.next;
  domains.value.push(...data.results);
  loadingNextPage.value = false;
}

function formatLastOverrideUpdated(domainData) {
  try {
    return formatRelative(parseISO(domainData.last_cmp_dco_date), new Date());
  } catch (err) {
    return "None";
  }
}

function isCMPEditable(domainData) {
  return domainData.product_type === "m1" || domainData.product_type === "m2";
}

function formatProductType(domainData) {
  return domainData.product_type.replace("_", " ");
}

const csvHref = computed(() => {
  const params = new URLSearchParams({
    ...apiRequestParams.value,
    format: "csv",
    no_pagination: "true",
  });
  return `/api/cmp/${vendorKey.value}/domains/?${params.toString()}`;
});

onMounted(async () => {
  await nextTick();
  textInput.value.focus();
  if (searchParams.name) {
    search();
  }
});
</script>
